import { v4 as uuidv4 } from 'uuid';
import { Vector2 } from '../utils/Vector2';
import { Tower, TowerType, Enemy } from '../types/GameTypes';
import { TOWER_STATS, UPGRADE_A_STATS, UPGRADE_B_STATS, UPGRADE_C_STATS } from '../types/TowerTypes';
import { ENEMY_TYPE_DATA } from '../types/EnemyTypes';

export class TowerSystem {
  private towers: Map<string, Tower> = new Map();

  createTower(type: TowerType, position: Vector2): Tower {
    const stats = TOWER_STATS[type];
    
    const tower: Tower = {
      id: uuidv4(),
      type,
      position: position.clone(),
      level: 'base',
      hp: stats.hp,
      maxHp: stats.hp,
      range: stats.range,
      damage: stats.damage,
      attackSpeed: stats.attackSpeed,
      lastFireTime: 0,
      baseAttackSpeed: stats.attackSpeed, // Store base attack speed
      aoeRadius: stats.aoeRadius,
      burnDamage: stats.burnDamage,
      slowAmount: stats.slowAmount,
      chainCount: stats.chainCount,
    };

    this.towers.set(tower.id, tower);
    return tower;
  }

  upgradeTower(tower: Tower, path: 'A' | 'B' | 'C'): boolean {
    if (tower.level === path || (tower.level === 'B' && path === 'A') || (tower.level === 'C' && (path === 'A' || path === 'B'))) {
      return false; // Already upgraded
    }
    
    // Upgrade B requires Upgrade A first
    if (path === 'B' && tower.level !== 'A') {
      return false; // Must have Upgrade A before B
    }
    
    // Upgrade C requires Upgrade B first
    if (path === 'C' && tower.level !== 'B') {
      return false; // Must have Upgrade B before C
    }
    
    // Only physical towers have Upgrade C
    if (path === 'C' && tower.type !== 'arrow' && tower.type !== 'cannon' && tower.type !== 'sniper') {
      return false;
    }

    const upgradeStats = path === 'A' 
      ? UPGRADE_A_STATS[tower.type] 
      : path === 'B' 
        ? UPGRADE_B_STATS[tower.type]
        : UPGRADE_C_STATS[tower.type as 'arrow' | 'cannon' | 'sniper'];
    
    // Apply upgrade stats
    if (upgradeStats.damage !== undefined) {
      tower.damage = upgradeStats.damage;
    } else if (path === 'A') {
      // Some upgrades don't change base damage, keep original
    }

    if (upgradeStats.attackSpeed !== undefined) {
      // For arrow tower Upgrade B (Rapid Fire), set base speed and prepare for first-shot trigger
      if (tower.type === 'arrow' && path === 'B') {
        tower.baseAttackSpeed = upgradeStats.attackSpeed; // Store the upgraded base speed (1.5s)
        tower.attackSpeed = upgradeStats.attackSpeed; // Start with base speed
        tower.lastAbilityTime = -1; // -1 means not yet triggered (will activate on first shot)
        tower.abilityActive = false;
        console.log(`[ARROW UPGRADE] Upgraded to B (Rapid Fire): baseAttackSpeed=${tower.baseAttackSpeed}, lastAbilityTime=${tower.lastAbilityTime}, abilityActive=${tower.abilityActive}`);
      } else {
      tower.attackSpeed = upgradeStats.attackSpeed;
        if (!tower.baseAttackSpeed) {
          tower.baseAttackSpeed = tower.attackSpeed; // Store base if not set
        }
      }
    }

    if (upgradeStats.range !== undefined) {
      tower.range = upgradeStats.range;
    }

    if (upgradeStats.aoeRadius !== undefined) {
      tower.aoeRadius = upgradeStats.aoeRadius;
    }

    if (upgradeStats.burnDamage !== undefined) {
      tower.burnDamage = upgradeStats.burnDamage;
    }

    if (upgradeStats.slowAmount !== undefined) {
      tower.slowAmount = upgradeStats.slowAmount;
    }

    if (upgradeStats.chainCount !== undefined) {
      tower.chainCount = upgradeStats.chainCount;
    }

    // Mark upgrade as purchased (cumulative)
    if (path === 'A') {
      tower.hasUpgradeA = true;
    } else if (path === 'B') {
      tower.hasUpgradeB = true;
    } else if (path === 'C') {
      tower.hasUpgradeC = true;
    }
    
    // Update level for display purposes (legacy)
    tower.level = path;
    return true;
  }

  findTargets(tower: Tower, enemies: Enemy[]): Enemy[] {
    const inRange: Enemy[] = [];
    
    // Convert tower position from tile to pixel coordinates
    const towerPixelPos = Vector2.fromTile(tower.position.x, tower.position.y, 32);
    const rangeInPixels = tower.range * 32; // 32 pixels per tile
    
    for (const enemy of enemies) {
      const distance = towerPixelPos.distance(enemy.position);
      
      if (distance <= rangeInPixels) {
        // Check if enemy can be hit by this tower type
        const canHit = this.canTowerHitEnemy(tower, enemy);
        if (canHit) {
          inRange.push(enemy);
        }
      }
    }

    return inRange;
  }

  canTowerHitEnemy(tower: Tower, enemy: Enemy): boolean {
    // All towers can now hit all enemies (ghost/flying rework)
    // The difference is in resistances, not targeting ability
    return true;
  }

  selectTarget(tower: Tower, targets: Enemy[]): Enemy | null {
    if (targets.length === 0) return null;

    // Sniper with Upgrade A: Target lowest HP enemies first
    if (tower.type === 'sniper' && tower.hasUpgradeA) {
      targets.sort((a, b) => {
        const aPercent = a.hp / a.maxHp;
        const bPercent = b.hp / b.maxHp;
        if (aPercent < 0.3 && bPercent >= 0.3) return -1;
        if (bPercent < 0.3 && aPercent >= 0.3) return 1;
        return aPercent - bPercent;
      });
      return targets[0];
    }

    // Lightning: Target highest HP for better chain
    if (tower.type === 'lightning') {
      targets.sort((a, b) => b.hp - a.hp);
      return targets[0];
    }

    // Cannon: Target densest group (closest to other enemies)
    if (tower.type === 'cannon' && tower.aoeRadius) {
      let bestTarget: Enemy | null = null;
      let maxNearby = 0;

      for (const target of targets) {
        let nearby = 0;
        for (const other of targets) {
          if (target.id !== other.id) {
            const dist = target.position.distance(other.position);
            if (dist <= tower.aoeRadius * 32) {
              nearby++;
            }
          }
        }
        if (nearby > maxNearby) {
          maxNearby = nearby;
          bestTarget = target;
        }
      }
      return bestTarget || targets[0];
    }

    // Default: Nearest enemy
    targets.sort((a, b) => {
      const distA = tower.position.distance(a.position);
      const distB = tower.position.distance(b.position);
      return distA - distB;
    });

    return targets[0];
  }

  canFire(tower: Tower, currentTime: number): boolean {
    if (tower.attackSpeed === 0) {
      // Constant AoE (Ice tower)
      return true;
    }

    // NOTE: Arrow tower passive ability is now handled in Game.ts update loop
    // This ensures proper timing and visual feedback integration

    const timeSinceLastFire = currentTime - tower.lastFireTime;
    const fireInterval = 1 / tower.attackSpeed;
    return timeSinceLastFire >= fireInterval;
  }

  fire(tower: Tower, target: Enemy, enemies: Enemy[], currentTime: number): {
    damage: number;
    damageType: 'physical' | 'elemental';
    targets: Enemy[];
    targetDamages?: Map<string, number>; // Per-target damage for cannon upgrade A outer ring
    effects?: { type: string; value: number; duration: number }[];
    wasCrit?: boolean;
    critDamage?: number;
    evaded?: boolean; // Attack was evaded
    evadedTargets?: Enemy[]; // For AoE/chain attacks with multiple evasions
    executedTargets?: Enemy[]; // Targets that were executed (instant kill)
    armorReductionTargets?: Enemy[]; // Targets that received armor reduction debuff
  } {
    tower.lastFireTime = currentTime;

    const isElemental = tower.type === 'fire' || tower.type === 'ice' || tower.type === 'lightning';
    const damageType: 'physical' | 'elemental' = isElemental ? 'elemental' : 'physical';

    let damage = tower.damage;
    const hitTargets: Enemy[] = [target];
    const effects: { type: string; value: number; duration: number }[] = [];
    const targetDamages = new Map<string, number>(); // For per-target damage (cannon upgrade A)
    const evadedTargets: Enemy[] = []; // Track which enemies evaded
    const executedTargets: Enemy[] = []; // Track executed targets
    const armorReductionTargets: Enemy[] = []; // Track targets that receive armor reduction

    // Check for evasion FIRST (before damage calculation) - True Strike bypasses this
    const isTrueStrike = tower.type === 'sniper'; // Sniper towers never miss
    if (!isTrueStrike) {
      const enemyData = ENEMY_TYPE_DATA[target.type];
      let evasionChance = enemyData.evasionChance;
      
      // Bosses: Apply wave-scaled evasion (stored in enemy.waveLevel if available)
      if (target.type === 'boss' && (target as any).waveLevel) {
        const calculateBossEvasion = (wave: number): number => {
          const baseEvasion = 0.1; // 10% base
          const waveBonus = Math.min(wave * 0.005, 0.15); // +0.5% per wave, capped at +15%
          return Math.min(baseEvasion + waveBonus, 0.25); // Max 25% evasion
        };
        evasionChance = calculateBossEvasion((target as any).waveLevel);
      }
      
      if (evasionChance > 0 && Math.random() < evasionChance) {
        // Attack evaded - return immediately
        return {
          damage: 0,
          damageType,
          targets: [target],
          evaded: true,
          evadedTargets: [target],
        };
      }
    }

    // Apply special abilities - Critical Hit (Arrow Upgrade A)
    let wasCrit = false;
    let critDamage = 0;
    let actualCritDamage = 0; // Track actual damage after resistances
    if (tower.type === 'arrow' && tower.hasUpgradeA) {
      // 25% crit chance
      if (Math.random() < 0.25) {
        damage *= 2;
        wasCrit = true;
        critDamage = damage;
      }
    }
    
    // Cannon Upgrade B: Apply armor reduction to all hit targets
    if (tower.type === 'cannon' && tower.hasUpgradeB) {
      effects.push({
        type: 'armor_reduction',
        value: 0.15, // 15% armor reduction
        duration: 5, // 5 seconds
      });
      // Track all hit targets for armor reduction indicator
      for (const hitTarget of hitTargets) {
        // Only show indicator if target doesn't already have armor reduction
        const hasArmorReduction = hitTarget.statusEffects.some(e => e.type === 'armor_reduction');
        if (!hasArmorReduction) {
          armorReductionTargets.push(hitTarget);
        }
      }
    }

    if (tower.type === 'cannon' && tower.aoeRadius) {
      // AoE damage
      const aoeRange = tower.aoeRadius * 32;
      const innerRingRadius = 2 * 32; // 2 tiles = inner ring (full damage)
      const outerRingRadius = 3 * 32; // 3 tiles = outer ring (50% damage) - only for upgrade A
      
      console.log(`[AOE DAMAGE] Cannon firing: aoeRadius=${tower.aoeRadius} tiles, aoeRange=${aoeRange}px, level=${tower.level}`);
      
      // Primary target always takes full damage
      targetDamages.set(target.id, damage);
      
      // Check if cannon has upgrade A (outer ring mechanic)
      const hasUpgradeA = tower.hasUpgradeA;
      
      for (const enemy of enemies) {
        if (enemy.id !== target.id) {
          const dist = target.position.distance(enemy.position);
          if (dist <= aoeRange) {
            hitTargets.push(enemy);
            
            // Calculate damage based on distance for upgrade A
            if (hasUpgradeA) {
              if (dist <= innerRingRadius) {
                // Inner ring (0-2 tiles): full damage
                targetDamages.set(enemy.id, damage);
              } else if (dist <= outerRingRadius) {
                // Outer ring (2-3 tiles): 50% damage
                targetDamages.set(enemy.id, damage * 0.5);
        }
            } else {
              // No upgrade A: all enemies in AoE take full damage
              targetDamages.set(enemy.id, damage);
            }
          }
        }
      }
    } else {
      // Non-cannon or no AoE: primary target takes base damage
      // (Other targets will be added later for lightning chain, etc.)
      targetDamages.set(target.id, damage);
    }

    if (tower.type === 'fire' && tower.burnDamage) {
      // Apply burn
      effects.push({
        type: 'burn',
        value: tower.burnDamage,
        duration: 3,
      });

      // Upgrade A: Burn spreads
      if (tower.hasUpgradeA) {
        // Spread to nearby enemies
        for (const enemy of enemies) {
          if (enemy.id !== target.id && hitTargets.findIndex(e => e.id === enemy.id) === -1) {
            const dist = target.position.distance(enemy.position);
            if (dist <= 64) { // 2 tiles
              hitTargets.push(enemy);
              targetDamages.set(enemy.id, damage); // Spread targets take same damage
              effects.push({
                type: 'burn',
                value: tower.burnDamage,
                duration: 3,
              });
            }
          }
        }
      }
    }

    if (tower.type === 'ice' && tower.slowAmount) {
      // Apply slow
      effects.push({
        type: 'slow',
        value: tower.slowAmount,
        duration: 2,
      });

      // Upgrade A: Freeze after 2s slow
      if (tower.hasUpgradeA) {
        // Check if enemy already has slow
        const hasSlow = target.statusEffects.some(e => e.type === 'slow');
        if (hasSlow) {
          effects.push({
            type: 'freeze',
            value: 1,
            duration: 1,
          });
        }
      }

      // Upgrade B: Shatter chance
      if (tower.hasUpgradeB && target.hp / target.maxHp < 0.1) {
        if (Math.random() < 0.1) {
          // Instant kill
          damage = target.hp;
        }
      }
    }

    if (tower.type === 'lightning' && tower.chainCount) {
      // Chain lightning
      const chained = new Set<string>([target.id]);
      let currentTarget = target;
      let chainDamage = damage;

      for (let i = 0; i < tower.chainCount && i < enemies.length; i++) {
        // Find next target
        let nextTarget: Enemy | null = null;
        let minDist = Infinity;

        for (const enemy of enemies) {
          if (!chained.has(enemy.id)) {
            const dist = currentTarget.position.distance(enemy.position);
            if (dist < minDist && dist <= 96) { // 3 tiles
              minDist = dist;
              nextTarget = enemy;
            }
          }
        }

        if (nextTarget) {
          hitTargets.push(nextTarget);
          chained.add(nextTarget.id);
          
          // Upgrade A: +15% damage per jump
          if (tower.hasUpgradeA) {
            chainDamage *= 1.15;
            // Update damage for chained targets (damage increases per jump)
            targetDamages.set(nextTarget.id, chainDamage);
          } else {
            // No upgrade: all chained targets take base damage
            targetDamages.set(nextTarget.id, damage);
          }
          
          currentTarget = nextTarget;
        } else {
          break;
        }
      }
      
      // Update base damage to chainDamage for lightning with upgrade A
      if (tower.type === 'lightning' && tower.hasUpgradeA && chainDamage !== damage) {
        // The final chainDamage is what should be used, but we've already set per-target damages
        // Keep base damage as is for primary target (already set)
      }

      // Upgrade B: Electrify stacks
      if (tower.hasUpgradeB) {
        for (const hitTarget of hitTargets) {
          if (Math.random() < 0.2) {
            effects.push({
              type: 'electrified',
              value: 0.05, // +5% per stack
              duration: 5,
            });
          }
        }
      }
    }

    if (tower.type === 'sniper' && tower.hasUpgradeB) {
      // Execute: 30% chance to insta-kill below 6% HP (not on elite/boss)
      for (const hitTarget of hitTargets) {
        const hpPercent = hitTarget.hp / hitTarget.maxHp;
        const isEliteOrBoss = hitTarget.isElite || hitTarget.type === 'boss';
        
        if (hpPercent < 0.06) {
          if (isEliteOrBoss) {
            // Elite/Boss are immune to execute - deal 1.5x crit damage instead
            damage = damage * 1.5;
            wasCrit = true;
            critDamage = damage;
            console.log(`[SNIPER EXECUTE] Elite/Boss immune to execute - dealing 1.5x crit damage: ${Math.floor(damage)}`);
          } else if (Math.random() < 0.30) {
            // 30% chance to execute normal enemies
            damage = hitTarget.hp; // Instant kill
            executedTargets.push(hitTarget); // Track execution for visual/sound feedback
            console.log(`[SNIPER EXECUTE] Execute triggered! Target HP: ${Math.floor(hitTarget.hp)}`);
          }
        }
      }
    }

    // Calculate actual crit damage after resistances AND armor reduction debuff for display
    if (wasCrit && target) {
      const enemyData = ENEMY_TYPE_DATA[target.type];
      let finalCritDamage = critDamage;
      
      // Apply base resistances
      if (damageType === 'physical') {
        let effectiveResistance = enemyData.physicalResistance;
        
        // Check for armor reduction debuff from Cannon Upgrade B
        const armorReduction = target.statusEffects.find(e => e.type === 'armor_reduction');
        if (armorReduction) {
          effectiveResistance = Math.max(0, effectiveResistance - armorReduction.value);
          console.log(`[CRIT CALC] Armor reduction applied: base ${enemyData.physicalResistance} -> effective ${effectiveResistance}`);
        }
        
        finalCritDamage *= (1 - effectiveResistance);
      } else if (damageType === 'elemental') {
        finalCritDamage *= enemyData.elementalVulnerability;
      }
      actualCritDamage = finalCritDamage;
    }

    return {
      damage,
      damageType,
      targets: hitTargets,
      targetDamages: targetDamages.size > 0 ? targetDamages : undefined,
      effects: effects.length > 0 ? effects : undefined,
      wasCrit,
      critDamage: wasCrit ? actualCritDamage : undefined, // Return actual damage taken
      executedTargets: executedTargets.length > 0 ? executedTargets : undefined,
      armorReductionTargets: armorReductionTargets.length > 0 ? armorReductionTargets : undefined,
    };
  }

  getTower(id: string): Tower | undefined {
    return this.towers.get(id);
  }

  getAllTowers(): Tower[] {
    return Array.from(this.towers.values());
  }

  removeTower(id: string): boolean {
    return this.towers.delete(id);
  }

  repairTower(tower: Tower): void {
    tower.hp = tower.maxHp;
  }

  damageTower(tower: Tower, amount: number): void {
    tower.hp = Math.max(0, tower.hp - amount);
  }

  isTowerDestroyed(tower: Tower): boolean {
    return tower.hp <= 0;
  }
}

